import { Link } from 'react-router';
import { Banner, Button, Caret, Skeleton, useToast } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { AvailabilityGrid } from './AvailabilityGrid';
import { CalendarConnectionsCard, ChannelsCard } from './ChannelCards';
import { useAvailability, useSaveAvailability } from './api';
import { useAvailabilityForm } from './availabilityForm';
import fields from './fields.module.css';
import styles from './AvailabilityPage.module.css';

/** "Europe/Berlin (GMT+2)" — the fixed timezone with its current offset. */
export function timezoneLabel(timezone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  }).formatToParts(now);
  const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  return offset ? `${timezone} (${offset})` : timezone;
}

/** Beratung 08 — `/calendar/availability`: week grid with „Speichern", calendar connections, channels. */
export function AvailabilityPage() {
  const mobile = useIsMobile();
  const { toast } = useToast();
  const availability = useAvailability();
  const save = useSaveAvailability();
  const form = useAvailabilityForm(availability.data);

  async function submit() {
    const body = form.prepare();
    if (!body) return;
    try {
      await save.mutateAsync(body);
      toast({ message: 'Verfügbarkeit gespeichert.' });
    } catch (error) {
      form.setServerError(error);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.grid}>
        <section className={styles.card} aria-labelledby="availability-title">
          <div className={styles.cardHead}>
            <h1 id="availability-title" className={styles.cardTitle}>
              Verfügbarkeit
            </h1>
            <Button
              size="small"
              className={styles.saveButton}
              loading={save.isPending}
              disabled={availability.isPending || availability.isError}
              onClick={() => void submit()}
            >
              Speichern
            </Button>
          </div>
          <p className={styles.cardText}>
            Wochenfenster — der verbundene Kalender schneidet Belegtes automatisch weg.
          </p>
          {mobile ? (
            <Link to="/settings/availability" className={styles.settingsLink}>
              Termintypen und Buchungslink in den Einstellungen
            </Link>
          ) : null}
          <div className={styles.timezone}>
            <span className={fields.label}>Zeitzone</span>
            <div className={styles.timezoneValue} aria-readonly="true">
              {timezoneLabel(availability.data?.timezone ?? 'Europe/Berlin')}
              <Caret size={11} />
            </div>
          </div>
          {save.isError && Object.keys(form.errors).length === 0 ? (
            <div className={styles.banner}>
              <Banner tone="danger">{errorMessage(save.error)}</Banner>
            </div>
          ) : null}
          {availability.isError ? (
            <div className={styles.banner}>
              <Banner
                tone="danger"
                actions={
                  <Button variant="secondary" size="small" onClick={() => void availability.refetch()}>
                    Erneut laden
                  </Button>
                }
              >
                {errorMessage(availability.error)}
              </Banner>
            </div>
          ) : availability.isPending ? (
            <div className={styles.skeleton} aria-busy="true">
              {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                <Skeleton key={index} height={38} />
              ))}
            </div>
          ) : (
            <div className={styles.gridWrap}>
              <AvailabilityGrid form={form} variant={mobile ? 'mobile' : 'sheet'} disabled={save.isPending} />
            </div>
          )}
        </section>
        <div className={styles.column}>
          <CalendarConnectionsCard />
          <ChannelsCard />
        </div>
      </div>
    </div>
  );
}
